/**
 * /admin/stage-jobs — cross-module async-job observability for DJ's
 * live-fire QA workflow.
 *
 * Filter persistence: query state is mirrored into ``window.location.search``
 * via ``replaceState`` so admins can share a filter URL. The app uses
 * state-based routing (not react-router); the URL params are read on
 * mount and written on every filter change.
 *
 * List refresh: polled every 10s (slower than per-job polling — list
 * view is informational, not operational).
 *
 * Detail drawer: in-page slide-out panel rather than a modal — matches
 * the DiagnosticPage expand-on-click pattern. Drawer lands in
 * Sub-Chunk 2 (next commit); this commit ships the list shell + the
 * row-click hook that the drawer will consume.
 *
 * Admin double-gate: App.js renders this only when isAdmin === true,
 * and the backend require_admin gate is the source of truth.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import {
  adminListStageJobs,
  type StageJobRow,
  type StageJobsListResponse,
  type AdminListStageJobsParams,
} from '@/api/client';
import StageJobDetailDrawer from '@/components/admin/StageJobDetailDrawer';

interface Props {
  onBack: () => void;
  /** Notified when a drafter clicks a row. Kept as an opt-in callback
   *  for tests / future embedding; the drawer is wired inline below
   *  so the standalone page doesn't depend on it. */
  onRowClick?: (job: StageJobRow) => void;
}

const POLL_MS = 10_000;
const PAGE_SIZE = 50;

const STATUS_CLASS: Record<string, string> = {
  succeeded:             'bg-emerald-100 text-emerald-800 border-emerald-300',
  running:               'bg-sky-100 text-sky-800 border-sky-300',
  failed:                'bg-rose-100 text-rose-800 border-rose-300',
  refused_all_providers: 'bg-amber-100 text-amber-800 border-amber-300',
  queued:                'bg-zinc-100 text-zinc-700 border-zinc-300',
  cancelled:             'bg-zinc-100 text-zinc-600 border-zinc-300',
};

const MODULE_CLASS: Record<string, string> = {
  claims:   'bg-indigo-100 text-indigo-800 border-indigo-300',
  drawing:  'bg-violet-100 text-violet-800 border-violet-300',
  spec:     'bg-teal-100   text-teal-800   border-teal-300',
  document: 'bg-zinc-100   text-zinc-700   border-zinc-300',
};

const STATUS_OPTIONS = [
  'queued', 'running', 'succeeded', 'failed',
  'refused_all_providers', 'cancelled',
];
const MODULE_OPTIONS = ['claims', 'drawing', 'spec', 'document'];

function _statusIcon(status: string): React.ReactNode {
  switch (status) {
    case 'succeeded':
      return <CheckCircle2 size={12} className="text-emerald-600" />;
    case 'failed':
    case 'refused_all_providers':
    case 'cancelled':
      return <XCircle size={12} className="text-rose-600" />;
    case 'running':
      return <Loader2 size={12} className="text-sky-600 animate-spin" />;
    default:
      return null;
  }
}

function _formatDuration(start: string | null, end: string | null): string {
  if (!start) return '—';
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  const sec = Math.max(0, (endMs - startMs) / 1000);
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const min = Math.floor(sec / 60);
  const rem = Math.floor(sec % 60);
  return `${min}m ${rem}s`;
}

function _formatCreatedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

// ─── URL <-> filter state mirroring ────────────────────────────────


function _readFiltersFromUrl(): AdminListStageJobsParams {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: AdminListStageJobsParams = {};
  const module = params.get('module');
  if (module) out.module = module;
  const stage = params.get('stage');
  if (stage) out.stage = stage;
  const action = params.get('action');
  if (action) out.action = action;
  const status = params.get('status');
  if (status) out.status = status;
  const matter = params.get('matter_id');
  if (matter) out.matter_id = matter;
  if (params.get('fallback_only') === 'true') out.fallback_only = true;
  const offset = params.get('offset');
  if (offset) out.offset = Number(offset);
  return out;
}


function _writeFiltersToUrl(filters: AdminListStageJobsParams): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams();
  if (filters.module)        params.set('module', filters.module);
  if (filters.stage)         params.set('stage', filters.stage);
  if (filters.action)        params.set('action', filters.action);
  if (filters.status)        params.set('status', filters.status);
  if (filters.matter_id)     params.set('matter_id', filters.matter_id);
  if (filters.fallback_only) params.set('fallback_only', 'true');
  if (filters.offset)        params.set('offset', String(filters.offset));
  const qs = params.toString();
  const newUrl = window.location.pathname + (qs ? `?${qs}` : '');
  try {
    window.history.replaceState({}, '', newUrl);
  } catch {
    // history API unavailable (sandboxed iframe / SSR pre-render) —
    // filter still applies in-memory.
  }
}


// ─── Component ─────────────────────────────────────────────────────


export default function StageJobsPage({ onBack, onRowClick }: Props) {
  const [filters, setFilters] = useState<AdminListStageJobsParams>(
    () => _readFiltersFromUrl(),
  );
  const [data, setData] = useState<StageJobsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track which row is selected so the row click feel works end-to-
  // end. Sub-Chunk 2's drawer wires this through.
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const params: AdminListStageJobsParams = useMemo(
    () => ({ ...filters, limit: PAGE_SIZE }),
    [filters],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminListStageJobs(params);
      setData(r);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load stage jobs';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Initial + filter-change load.
  useEffect(() => {
    _writeFiltersToUrl(filters);
    void load();
  }, [filters, load]);

  // Background poll every 10s. Doesn't reset on filter change because
  // the filter-change effect above already triggered a load.
  useEffect(() => {
    const id = window.setInterval(() => {
      void load();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  function patchFilter<K extends keyof AdminListStageJobsParams>(
    key: K, value: AdminListStageJobsParams[K] | null,
  ) {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === null || value === undefined || value === '') {
        delete next[key];
      } else {
        next[key] = value as AdminListStageJobsParams[K];
      }
      // Reset offset whenever filters change — page 1 of new query.
      delete next.offset;
      return next;
    });
  }

  function handleRowClick(job: StageJobRow) {
    setSelectedJobId(job.rq_job_id);
    onRowClick?.(job);
  }

  function handlePage(direction: -1 | 1) {
    const cur = filters.offset || 0;
    const next = Math.max(0, cur + direction * PAGE_SIZE);
    setFilters((prev) => ({ ...prev, offset: next }));
  }

  const offset = filters.offset || 0;

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="admin-stage-jobs-page">
      {/* Header strip — matches DiagnosticPage chrome */}
      <div className="border-b border-zinc-200 bg-white px-4 py-2 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-zinc-600 hover:text-zinc-900"
          data-testid="admin-stage-jobs-back"
        >
          ← Back
        </button>
        <h1 className="text-sm font-semibold text-zinc-900">Stage jobs</h1>
        <span className="text-[10px] text-zinc-500">
          cross-module async-job observability
        </span>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          title="Refresh"
          data-testid="admin-stage-jobs-refresh"
          className="ml-auto text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {loading
            ? <Loader2 size={12} className="animate-spin" />
            : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      {/* Filters strip */}
      <div className="border-b border-zinc-200 bg-white px-4 py-2 flex flex-wrap items-center gap-3 text-xs">
        <label className="inline-flex items-center gap-1">
          <span className="text-zinc-600">Module</span>
          <select
            data-testid="admin-stage-jobs-filter-module"
            value={filters.module || ''}
            onChange={(e) => patchFilter('module', e.target.value || null)}
            className="border border-zinc-300 rounded px-1 py-0.5"
          >
            <option value="">all</option>
            {MODULE_OPTIONS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-1">
          <span className="text-zinc-600">Status</span>
          <select
            data-testid="admin-stage-jobs-filter-status"
            value={filters.status || ''}
            onChange={(e) => patchFilter('status', e.target.value || null)}
            className="border border-zinc-300 rounded px-1 py-0.5"
          >
            <option value="">all</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-1">
          <span className="text-zinc-600">Stage</span>
          <input
            type="text"
            data-testid="admin-stage-jobs-filter-stage"
            value={filters.stage || ''}
            onChange={(e) => patchFilter('stage', e.target.value || null)}
            placeholder="stage_3"
            className="border border-zinc-300 rounded px-1 py-0.5 w-24"
          />
        </label>
        <label className="inline-flex items-center gap-1">
          <span className="text-zinc-600">Action</span>
          <input
            type="text"
            data-testid="admin-stage-jobs-filter-action"
            value={filters.action || ''}
            onChange={(e) => patchFilter('action', e.target.value || null)}
            placeholder="claim_generation"
            className="border border-zinc-300 rounded px-1 py-0.5 w-32"
          />
        </label>
        <label className="inline-flex items-center gap-1">
          <span className="text-zinc-600">Matter</span>
          <input
            type="text"
            data-testid="admin-stage-jobs-filter-matter"
            value={filters.matter_id || ''}
            onChange={(e) => patchFilter('matter_id', e.target.value || null)}
            placeholder="matter uuid"
            className="border border-zinc-300 rounded px-1 py-0.5 w-44 font-mono"
          />
        </label>
        <label className="inline-flex items-center gap-1">
          <input
            type="checkbox"
            data-testid="admin-stage-jobs-filter-fallback"
            checked={Boolean(filters.fallback_only)}
            onChange={(e) =>
              patchFilter('fallback_only', e.target.checked || null)
            }
          />
          <span className="text-zinc-600">fallback only</span>
        </label>
        {Object.keys(filters).length > 0 && (
          <button
            type="button"
            data-testid="admin-stage-jobs-filter-clear"
            onClick={() => setFilters({})}
            className="ml-auto text-[10px] text-zinc-500 hover:text-zinc-900 underline"
          >
            clear filters
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div
          data-testid="admin-stage-jobs-error"
          className="bg-rose-50 border-b border-rose-200 text-rose-800 text-xs px-4 py-2"
        >
          {error}
        </div>
      )}

      {/* Module-level summary cards (24h window from the backend
          summary_24h aggregate). Each card click pre-applies the
          corresponding filter. Backed by the same poll cadence as
          the table (10s); no separate fetch. */}
      {data?.summary_24h && Object.keys(data.summary_24h).length > 0 && (
        <div
          data-testid="admin-stage-jobs-summary-cards"
          className="border-b border-zinc-200 bg-white px-4 py-3 grid grid-cols-4 gap-3"
        >
          {MODULE_OPTIONS.map((mod) => {
            const card = data.summary_24h[mod] ||
                         { total: 0, by_status: {} };
            const failed = (card.by_status['failed'] || 0)
                         + (card.by_status['refused_all_providers'] || 0);
            return (
              <div
                key={mod}
                data-testid={`admin-stage-jobs-summary-card-${mod}`}
                className="border border-zinc-200 rounded p-2"
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={
                      'inline-block px-1.5 py-0.5 text-[10px] font-medium ' +
                      'border rounded ' + (MODULE_CLASS[mod] || 'bg-zinc-100')
                    }
                  >
                    {mod}
                  </span>
                  <span className="text-[10px] text-zinc-500">24h</span>
                </div>
                <div className="text-lg font-semibold text-zinc-900">
                  {card.total}
                </div>
                <div className="flex items-center gap-2 mt-1 text-[10px]">
                  <button
                    type="button"
                    data-testid={`admin-stage-jobs-summary-card-${mod}-failed`}
                    onClick={() => setFilters({ module: mod, status: 'failed' })}
                    disabled={failed === 0}
                    className={
                      'inline-flex items-center gap-0.5 underline ' +
                      (failed > 0
                        ? 'text-rose-700 hover:text-rose-900'
                        : 'text-zinc-400 cursor-default')
                    }
                  >
                    <XCircle size={10} />
                    {failed} failed
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-stage-jobs-summary-card-${mod}-fallback`}
                    onClick={() => setFilters({ module: mod, fallback_only: true })}
                    className="inline-flex items-center gap-0.5 underline text-amber-700 hover:text-amber-900"
                    title="Jobs where the refusal-aware fallback chain advanced (forward-compat — 0 today)"
                  >
                    <AlertTriangle size={10} />
                    fallback
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-zinc-100 border-b border-zinc-200">
            <tr className="text-left text-zinc-600">
              <th className="px-3 py-1.5 font-medium">Created</th>
              <th className="px-3 py-1.5 font-medium">Module</th>
              <th className="px-3 py-1.5 font-medium">Stage / action</th>
              <th className="px-3 py-1.5 font-medium">Status</th>
              <th className="px-3 py-1.5 font-medium">Matter</th>
              <th className="px-3 py-1.5 font-medium">Provider</th>
              <th className="px-3 py-1.5 font-medium">Fallback</th>
              <th className="px-3 py-1.5 font-medium">Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {data?.items.map((job) => {
              const isSelected = selectedJobId === job.rq_job_id;
              return (
                <tr
                  key={job.id}
                  data-testid={`admin-stage-jobs-row-${job.rq_job_id}`}
                  onClick={() => handleRowClick(job)}
                  className={
                    'cursor-pointer hover:bg-sky-50 ' +
                    (isSelected ? 'bg-sky-100' : 'bg-white')
                  }
                >
                  <td className="px-3 py-1.5 font-mono text-zinc-700">
                    {_formatCreatedAt(job.created_at)}
                  </td>
                  <td className="px-3 py-1.5">
                    <span
                      className={
                        'inline-block px-1.5 py-0.5 text-[10px] font-medium ' +
                        'border rounded ' + (MODULE_CLASS[job.module] || 'bg-zinc-100')
                      }
                    >
                      {job.module}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-zinc-700">
                    {job.stage}
                    {job.action && job.action !== job.stage && (
                      <span className="text-zinc-400"> / {job.action}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <span
                      className={
                        'inline-flex items-center gap-1 px-1.5 py-0.5 ' +
                        'text-[10px] font-medium border rounded ' +
                        (STATUS_CLASS[job.status] || 'bg-zinc-100')
                      }
                    >
                      {_statusIcon(job.status)}
                      {job.status}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-zinc-500">
                    {job.matter_id?.slice(0, 8) || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-zinc-700">
                    {job.final_provider || '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    {job.fallback_count > 0 ? (
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300 rounded"
                        title="Primary provider failed; ran on fallback"
                      >
                        <AlertTriangle size={10} />×{job.fallback_count}
                      </span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-zinc-700">
                    {_formatDuration(job.started_at, job.finished_at)}
                  </td>
                </tr>
              );
            })}
            {data && data.items.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-zinc-500">
                  No stage jobs match the current filters.
                </td>
              </tr>
            )}
            {!data && loading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-zinc-500">
                  <Loader2 size={14} className="inline animate-spin mr-1" />
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="border-t border-zinc-200 bg-white px-4 py-2 flex items-center gap-3 text-xs">
        <span className="text-zinc-600">
          {data ? `${offset + 1}–${offset + (data.items.length)} of …` : '—'}
        </span>
        <button
          type="button"
          data-testid="admin-stage-jobs-page-prev"
          onClick={() => handlePage(-1)}
          disabled={offset === 0 || loading}
          className="px-2 py-0.5 border border-zinc-300 rounded disabled:opacity-50"
        >
          ← Prev
        </button>
        <button
          type="button"
          data-testid="admin-stage-jobs-page-next"
          onClick={() => handlePage(1)}
          disabled={!data || data.items.length < PAGE_SIZE || loading}
          className="px-2 py-0.5 border border-zinc-300 rounded disabled:opacity-50"
        >
          Next →
        </button>
        <span className="ml-auto text-[10px] text-zinc-400 font-mono">
          polls every {POLL_MS / 1000}s
        </span>
      </div>

      {/* Detail drawer — slides in from the right when a row is
          selected. ``onRerunQueued`` triggers a list reload so the
          new lifecycle observations land in the table. */}
      <StageJobDetailDrawer
        jobId={selectedJobId}
        onClose={() => setSelectedJobId(null)}
        onRerunQueued={() => void load()}
      />
    </div>
  );
}
